import { useEffect, useRef, useState } from "react";

/**
 * Adds `in-view` class to any element with `.reveal` when it enters the viewport.
 * Idempotent + safe on repeat mounts. Call once at the app or page root.
 */
export function useScrollReveal(rootSelector = ".reveal") {
    useEffect(() => {
        const els = Array.from(document.querySelectorAll(rootSelector));
        if (!("IntersectionObserver" in window)) {
            els.forEach((e) => e.classList.add("in-view"));
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("in-view");
                        io.unobserve(entry.target);
                    }
                });
            },
            { rootMargin: "0px 0px -12% 0px", threshold: 0.08 }
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [rootSelector]);
}

/**
 * Animated integer/decimal counter that runs when its ref becomes visible.
 * Returns [ref, displayValue].
 */
export function useCountUp(target, { duration = 1400, decimals = 0, prefix = "", suffix = "" } = {}) {
    const ref = useRef(null);
    const [value, setValue] = useState(0);
    const startedRef = useRef(false);

    useEffect(() => {
        if (!ref.current) return;
        const node = ref.current;
        const start = () => {
            if (startedRef.current) return;
            startedRef.current = true;
            const t0 = performance.now();
            const from = 0;
            const to = Number(target) || 0;
            const step = (now) => {
                const p = Math.min(1, (now - t0) / duration);
                const eased = 1 - Math.pow(1 - p, 3);
                setValue(from + (to - from) * eased);
                if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        };
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && start()),
            { threshold: 0.35 }
        );
        io.observe(node);
        return () => io.disconnect();
    }, [target, duration]);

    const formatted = `${prefix}${value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${suffix}`;
    return [ref, formatted];
}

/**
 * Tracks page scrollY for parallax offsets. Throttled with rAF.
 */
export function useParallax() {
    const [y, setY] = useState(0);
    useEffect(() => {
        let ticking = false;
        const on = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    setY(window.scrollY || 0);
                    ticking = false;
                });
                ticking = true;
            }
        };
        window.addEventListener("scroll", on, { passive: true });
        return () => window.removeEventListener("scroll", on);
    }, []);
    return y;
}
